/**
 * Pet Mindful · Ficha de producto
 *
 * Galería con miniaturas, selector de variantes en tarjetas y stepper de
 * cantidad. El CTA muestra el total (precio x cantidad), que es lo que pide
 * el diseño y lo que evita la sorpresa en el carrito.
 */

/* --------------------------------------------------------------- galería -- */
class PMGallery extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-pm-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-pm-thumb]'));

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.show(thumb.dataset.pmThumb));
    });

    // En móvil la galería es un carrusel con scroll nativo; los puntos
    // siguen a la slide visible. En escritorio el track no scrollea y esto
    // no hace nada.
    this.track = this.querySelector('[data-pm-track]');
    this.dots = Array.from(this.querySelectorAll('[data-pm-dot]'));
    if (this.track && this.dots.length > 1) {
      let frame = null;
      this.track.addEventListener(
        'scroll',
        () => {
          if (frame) return;
          frame = window.requestAnimationFrame(() => {
            frame = null;
            // Las imágenes de otra variante están ocultas: la posición del
            // scroll cuenta solo las visibles.
            const width = this.track.clientWidth || 1;
            const visible = this.dots.filter((dot) => !dot.hidden);
            const current = visible[Math.round(this.track.scrollLeft / width)];
            this.dots.forEach((dot) => dot.classList.toggle('is-active', dot === current));
          });
        },
        { passive: true }
      );
    }

    // Cada variante muestra su grupo de imágenes (ver el Liquid de la
    // galería). Sin grupos en el producto esto no cambia nada.
    if (this.slides.some((slide) => slide.dataset.pmGroup)) {
      document.addEventListener('pm:variant:change', (event) => {
        const media = event.detail.variant.featured_media;
        this.filter(media ? String(media.id) : '');
      });
    }
  }

  filter(group) {
    // Índices de las imágenes que se ven: las del grupo y las comunes
    const shown = new Set(
      this.slides
        .filter((slide) => !group || !slide.dataset.pmGroup || slide.dataset.pmGroup === group)
        .map((slide) => slide.dataset.pmSlide)
    );

    this.slides.forEach((slide) => (slide.hidden = !shown.has(slide.dataset.pmSlide)));
    this.thumbs.forEach((thumb) => (thumb.hidden = !shown.has(thumb.dataset.pmThumb)));
    this.dots.forEach((dot) => (dot.hidden = !shown.has(dot.dataset.pmDot)));

    const visible = this.slides.filter((slide) => !slide.hidden);
    const many = visible.length > 1;
    const thumbs = this.querySelector('.pm-pdp__thumbs');
    const dots = this.querySelector('.pm-pdp__dots');
    if (thumbs) thumbs.hidden = !many;
    if (dots) dots.hidden = !many;

    if (!visible.length) return;
    const first = visible[0].dataset.pmSlide;
    this.show(first);
    this.dots.forEach((dot) => dot.classList.toggle('is-active', dot.dataset.pmDot === first));
    if (this.track) this.track.scrollLeft = 0;
  }

  show(index) {
    this.slides.forEach((slide) => {
      slide.classList.toggle('is-active', slide.dataset.pmSlide === index);
    });
    this.thumbs.forEach((thumb) => {
      const active = thumb.dataset.pmThumb === index;
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-selected', String(active));
    });
  }
}

/* ------------------------------------------------------------- variantes -- */
class PMVariants extends HTMLElement {
  connectedCallback() {
    const data = this.querySelector('[data-pm-variants]');
    if (!data) return;

    try {
      this.variants = JSON.parse(data.textContent);
    } catch (error) {
      return;
    }

    this.form = this.closest('form');
    this.radios = Array.from(this.querySelectorAll('.pm-pdp__radio'));
    this.radios.forEach((radio) => {
      radio.addEventListener('change', () => this.update());
    });

    this.update({ silent: true });
  }

  selected() {
    const chosen = [];
    this.radios
      .filter((radio) => radio.checked)
      .forEach((radio) => chosen.push(radio.value));
    return chosen;
  }

  update(options = {}) {
    const chosen = this.selected();
    const variant = this.variants.find((candidate) =>
      candidate.options.every((value, index) => value === chosen[index])
    );

    // Marca como no disponibles las tarjetas que no llevan a ninguna variante
    this.radios.forEach((radio) => {
      const label = radio.nextElementSibling;
      if (label) label.classList.remove('is-unavailable');
    });

    if (!variant) return;

    const idField = this.form && this.form.querySelector('[data-pm-variant-id]');
    if (idField) idField.value = variant.id;

    document.dispatchEvent(
      new CustomEvent('pm:variant:change', { detail: { variant, silent: options.silent } })
    );

    if (!options.silent && this.dataset.url) {
      const url = new URL(this.dataset.url, window.location.origin);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.pathname + url.search);
    }
  }
}

/* ----------------------------------------------------- cantidad y totales -- */
function money(cents) {
  // COP no usa decimales; el separador de miles es el punto
  const value = Math.round(cents / 100);
  return '$' + value.toLocaleString('es-CO');
}

function bindQuantityAndTotal() {
  const form = document.getElementById('pm-product-form');
  if (!form) return;

  const input = form.querySelector('[data-pm-qty-input]');
  const totals = document.querySelectorAll('[data-pm-total]');
  const prices = document.querySelectorAll('[data-pm-price]');
  const ship = document.querySelector('[data-pm-ship]');
  const shipText = ship && ship.querySelector('[data-pm-ship-text]');
  // Precio de partida desde Liquid: sin variantes no llega ningún
  // pm:variant:change, y con variantes el primero se dispara antes de que
  // este listener exista. Sin esto el total y el envío no seguían la cantidad.
  const addButtonInit = form.querySelector('[data-pm-add]');
  let unitPrice = addButtonInit ? parseInt(addButtonInit.dataset.pmUnit, 10) : NaN;
  if (Number.isNaN(unitPrice)) unitPrice = null;

  // Envío gratis proyectado: carrito actual + lo que se va a agregar
  const renderShipping = (qty) => {
    if (!ship || !shipText || unitPrice === null) return;
    const free = parseInt(ship.dataset.free, 10) || 0;
    const subtotal = parseInt(ship.dataset.subtotal, 10) || 0;
    const missing = free - (subtotal + Math.round((unitPrice * qty) / 100));
    shipText.innerHTML =
      missing > 0
        ? `Con este producto te faltan <strong>${money(missing * 100)}</strong> para el envío gratis`
        : 'Con este producto tu envío es <strong>gratis</strong>';
  };

  const render = () => {
    if (unitPrice === null) return;
    const qty = Math.max(1, parseInt(input && input.value, 10) || 1);
    totals.forEach((node) => {
      node.textContent = money(unitPrice * qty);
    });
    renderShipping(qty);
  };

  // Tras agregar o cambiar algo en el carrito lateral, el subtotal de base
  // cambió: se vuelve a pedir y se recalcula el mensaje.
  document.addEventListener('pm:cart:updated', () => {
    if (!ship) return;
    fetch('/cart.js')
      .then((response) => response.json())
      .then((cart) => {
        ship.dataset.subtotal = String(Math.round(cart.total_price / 100));
        render();
      })
      .catch(() => {});
  });

  form.querySelectorAll('[data-pm-qty]').forEach((button) => {
    button.addEventListener('click', () => {
      if (!input) return;
      const step = parseInt(button.dataset.pmQty, 10);
      input.value = Math.max(1, (parseInt(input.value, 10) || 1) + step);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  });

  if (input) input.addEventListener('change', render);

  document.addEventListener('pm:variant:change', (event) => {
    const variant = event.detail.variant;
    unitPrice = variant.price;

    prices.forEach((node) => {
      node.textContent = money(variant.price);
    });

    const addButton = form.querySelector('[data-pm-add]');
    const addText = form.querySelector('[data-pm-add-text]');
    if (addButton && addText) {
      addButton.disabled = !variant.available;
      if (!variant.available) {
        addText.textContent = 'Agotado';
      } else if (!addText.querySelector('[data-pm-total]')) {
        addText.innerHTML = 'Agregar al carrito · <span data-pm-total></span>';
      }
    }
    render();
  });
}

/* ----------------------------------------------------- videos de clientes -- */
// La miniatura es un botón; al tocarlo se cambia por el video y arranca.
document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-pm-clip]');
  if (!button) return;

  const video = document.createElement('video');
  video.src = button.dataset.pmClip;
  video.controls = true;
  video.playsInline = true;
  video.autoplay = true;
  video.setAttribute('aria-label', button.getAttribute('aria-label') || '');
  button.replaceWith(video);
  video.play().catch(() => {});
});

if (!customElements.get('pm-gallery')) customElements.define('pm-gallery', PMGallery);
if (!customElements.get('pm-variants')) customElements.define('pm-variants', PMVariants);

// Una sola vez: el script es defer y corre con readyState «interactive»,
// antes de DOMContentLoaded. Con las dos llamadas los listeners quedaban
// duplicados y el «+» de cantidad sumaba de a 2.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bindQuantityAndTotal);
} else {
  bindQuantityAndTotal();
}
